import type { PortalWidgetSettings } from "@entrilia/shared/api/portalWidgetsTypes";
import type { AccessPermission, ImpersonationTargetType } from "@entrilia/shared/api/types";
import type { ConsentAgreement, ConsentAgreementType } from "@entrilia/shared/components/consentAgreement/types";
import type { FundraisingInfo } from "../../../api/types/fundraisingTypes";

export interface UserState {
  authenticated: boolean;
  email: string;
  permissions: ClientPermissionSet[];
  identity?: Identity;
  loading: IdentityLoading;
  selectedInvestorId?: string;
  isImpersonationMode: boolean;
  impersonatedAccessToAllEntities?: boolean;
  clientCode: string;
  viewOnlyFileProtection?: ViewOnlyFileProtection;
  sessionClientCode?: string;
}

export interface UserPermissionsResponse {
  email: string;
  permissions: ClientPermissionSet[];
  impersonationTargetType?: ImpersonationTargetType;
  impersonatedAccessToAllEntities?: boolean;
  fp?: ViewOnlyFileProtectionWire;
  sessionClientCode?: string; // The client the backend recorded this session against.
}

// Terse by design — the backend hides the scheme from API scrapers.
export interface ViewOnlyFileProtectionWire {
  n: string;
  k: ViewOnlyProtectionKeyWire[];
}

export interface ViewOnlyProtectionKeyWire {
  v: number;
  d: string;
}

export interface ViewOnlyFileProtection {
  parameterName: string;
  keys: ViewOnlyProtectionKey[];
}

export interface ViewOnlyProtectionKey {
  version: number;
  // base64url-encoded raw 32-byte AES-256-GCM key.
  key: string;
}

// `answered: false` means the server could not be asked — never that protection is off.
export type ProtectionProbe = { answered: true; protection?: ViewOnlyFileProtection } | { answered: false };

export interface ClientPermissionSet {
  clientCode: string;
  clientTitle: string;
  contactName: string;
  contactEmail: string;
  investorsPermissions: InvestorsPermissionSet[];
  entityPermissions: EntityPermissionSet[];
  portalSettings: PortalSettings;
  unsignedConsents?: ConsentAgreement[];
  fundraisings: FundraisingInfo[];
  messageCategoryIds: string[];
  portfolioMonitoringApplication?: PortfolioMonitoringApplication;
  featureToggles: Partial<Record<FeatureToggleName, boolean>>;
}

interface PortfolioMonitoringApplication {
  dataCollection?: {
    enabled: boolean;
  };
}

type FeatureToggleName = "InvestorPortalNewsEnabled";

export interface InvestorsPermissionSet {
  investorId: string;
  investorTitle: string;
  fundInvestors: InvestorFundPermissionSet[];
  investorLevelPermissions: AccessPermission[];
}

interface EntityPermissionSet {
  objectId: string;
  objectName: string;
  role: EntityPortalRole;
}

type EntityPortalRole = "Portfolio_Monitoring_Portal_Viewer" | "Portfolio_Monitoring_Portal_Member";

export interface PortalSettings {
  enabledFeatures?: string;
  enabledPreviewOnlyFeatures?: string;
  appBarColor?: string;
  appBarTextColor?: string;
  mainColor?: string;
  logoTypeUrl?: string;
  logoTypeSize?: RelativeLogoSize;
  logoMarkUrl?: string;
  faviconUrl?: string;
  bannerUrl?: string;
  bannerFontColor?: string;
  supportEmail?: string;
  supportPhone?: string;
  contactInvitationEmailCompanyLogoUrl?: string;
  inboxPageName?: string;
  widgetSettings?: PortalWidgetSettings;
}

export type RelativeLogoSize = "Small" | "Medium" | "Large";

export const allInvestorRelationPortalFeatures = [
  "Inbox",
  "Dashboards",
  "News",
  "AccountSettingsViewer",
  "AccountSettingsEditor",
  "BankAccountsDetailsViewer",
  "BankAccountsDetailsEditor",
  "Membership",
  "MembershipEditor",
  "PortfolioCompanies",
  "Fundraising",
  "DataCollection",
] as const;

export type InvestorRelationPortalFeatures = (typeof allInvestorRelationPortalFeatures)[number];

export interface InvestorFundPermissionSet {
  fundInvestorId: string;
  fundId: string;
  fundName: string;
  fundStage?: FundStage;
  permissions: AccessPermission[];
}

type FundStage = "Investment" | "Fundraising";

export interface Identity {
  id: string;
  name: string;
  phone: string;
  email: string;
  signInEmail: string;
  avatarImage?: string;
  largeImage?: boolean;
  consentAgreements?: ConsentAgreement[];
}

export interface UpdateIdentityRequest {
  name?: string;
  phone?: string;
  email?: string;
  avatarImage?: string;
  consentAgreements?: ConsentAgreement[];
}

export interface AcceptedConsentAgreement {
  type: ConsentAgreementType;
  version: number;
  name: string;
}

export interface IdentityLoading {
  identity: boolean;
  state?: IdentityLoadingState;
}

export enum IdentityLoadingState {
  None = 0,
  Loaded = 2,
  LoadingError = 3,
  LoadingErrorImpersonationPermissions = 4,
  AuthorizationOrAccessError = 5,
}
